// ============================================================
// Nuova prenotazione modal.
//
// Covers four sources (Web / Telefono / Ospite / Walk-in). A walk-in
// pre-fills time = now and status = seated so the owner can record
// someone who just sat down with two clicks. Other sources default
// to "new" status and let the owner pick a time.
// ============================================================
import { useMemo, useRef, useState, type FormEvent } from 'react';
import type { ReservationSource, ReservationStatus, ServiceId } from '../data/types';
import { useDashboard, useDashboardActions } from '../data/context';
import { t } from '../i18n/t';
import { Icon } from '../components/Icon';
import { Switch } from '../components/Switch';
import { TimeSelect } from './TimeSelect';
import { useFocusTrap } from '../components/useFocusTrap';

interface NewReservationModalProps {
  open: boolean;
  onClose: () => void;
  /** Default to 'cena' (matches the dashboard's default service tab). */
  defaultService?: ServiceId;
}

const SOURCES: { id: ReservationSource; labelKey: string }[] = [
  { id: 'web', labelKey: 'src_web' },
  { id: 'phone', labelKey: 'src_phone' },
  { id: 'guest', labelKey: 'src_guest' },
  { id: 'walkin', labelKey: 'src_walkin' },
];

/** "HH:mm" snapped to the next 15-minute mark. */
function nowHHMM(): string {
  const d = new Date();
  // Round up to nearest 15.
  const mins = Math.ceil(d.getMinutes() / 15) * 15;
  d.setMinutes(mins, 0, 0);
  return d.toTimeString().slice(0, 5);
}

export function NewReservationModal({
  open,
  onClose,
  defaultService = 'cena',
}: NewReservationModalProps) {
  const { tables, services, dateLabel, day } = useDashboard();
  const actions = useDashboardActions();
  const lang = 'it'; // modal copy is IT — owner-facing; matches the rest of the dashboard

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [covers, setCovers] = useState(2);
  const [service, setService] = useState<ServiceId>(defaultService);
  const [start, setStart] = useState(nowHHMM());
  const [tableLabel, setTableLabel] = useState<string>('');
  const [source, setSource] = useState<ReservationSource>('phone');
  const [note, setNote] = useState('');
  // Walk-in: pre-seat by default. Others: 'new'.
  const [seatNow, setSeatNow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useFocusTrap(modalRef, open, onClose);

  // When the user switches the source to walk-in, default seat-now to true
  // and clamp time to now. Reset when switching away.
  const onSourceChange = (s: ReservationSource) => {
    setSource(s);
    if (s === 'walkin') {
      setSeatNow(true);
      setStart(nowHHMM());
    } else {
      setSeatNow(false);
    }
  };

  const reset = () => {
    setName('');
    setPhone('');
    setCovers(2);
    setService(defaultService);
    setStart(nowHHMM());
    setTableLabel('');
    setSource('phone');
    setNote('');
    setSeatNow(false);
    setErrMsg(null);
  };

  const tablesInService = useMemo(() => tables, [tables]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!name.trim()) {
      setErrMsg('Inserisci il nome.');
      return;
    }
    setBusy(true);
    setErrMsg(null);

    // Orario del muro salvato come UTC (convenzione di piattaforma):
    // il giorno è quello selezionato col date stepper.
    const startsAt = `${day}T${start}:00.000Z`;
    const status: ReservationStatus = seatNow ? 'seated' : 'new';

    try {
      // The API expects a UUID for tableId; the owner-portal carries the
      // DB UUID in Table.dbId (set when loaded from the API). In mock
      // mode dbId is undefined — the action handler stores the label
      // directly in the reservation.
      const matched = tablesInService.find((tb) => tb.id === tableLabel);
      const tableIdField = matched ? (matched.dbId ?? matched.id) : null;

      await actions.createReservation({
        name: name.trim(),
        phone: phone.trim() || null,
        covers,
        startsAt,
        service,
        tableId: tableIdField,
        source,
        status,
        durationMinutes: 105,
        tags: [],
        noteIt: note.trim() || null,
        noteEn: null,
      });
      reset();
      onClose();
    } catch (err) {
      console.error('[new-resv] create failed:', err);
      setErrMsg('Impossibile creare la prenotazione.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="mscrim open" onMouseDown={onClose}>
      <div
        ref={modalRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-resv-title"
        onMouseDown={(e) => e.stopPropagation()}
        style={{ width: 480 }}
      >
        <div className="modal-head">
          <h3 id="new-resv-title">{t('newResv', lang)}</h3>
          <button type="button" className="dr-close" onClick={onClose} aria-label="Chiudi">
            <Icon name="x" size={18} />
          </button>
        </div>

        {/*
          The form needs to be a proper flex column itself so the
          modal-body inside it can grow + scroll while the modal-foot
          stays pinned. Without this — and because the form is the only
          flex child of .modal — the foot would render below the modal
          box on tall content.
        */}
        <form
          onSubmit={submit}
          noValidate
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <div className="modal-body">
            <div className="field">
              <label htmlFor="nr-name">{t('name', lang)}</label>
              <input
                id="nr-name"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Es. Marco Fontana"
                autoFocus
                required
              />
            </div>

            <div className="field">
              <label htmlFor="nr-phone">{t('phone', lang)}</label>
              <input
                id="nr-phone"
                className="input tnum"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+39 …"
              />
            </div>

            <div className="field-row">
              <div className="field" style={{ maxWidth: 110 }}>
                <label htmlFor="nr-covers">{t('party', lang)}</label>
                <input
                  id="nr-covers"
                  className="input tnum"
                  type="number"
                  min={1}
                  max={50}
                  value={covers}
                  onChange={(e) => setCovers(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                />
              </div>
              <div className="field">
                <label>Servizio</label>
                <div className="seg" style={{ height: 38 }}>
                  {services.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className={service === s.id ? 'on' : ''}
                      onClick={() => setService(s.id)}
                      style={{ flex: 1 }}
                    >
                      {s.name[lang]}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="field-row">
              <div className="field" style={{ maxWidth: 130 }}>
                <label htmlFor="nr-time">{t('time', lang)}</label>
                <TimeSelect id="nr-time" value={start} onChange={setStart} />
              </div>
              <div className="field">
                <label htmlFor="nr-table">{t('table', lang)}</label>
                <select
                  id="nr-table"
                  className="input"
                  value={tableLabel}
                  onChange={(e) => setTableLabel(e.target.value)}
                >
                  <option value="">— nessuno —</option>
                  {tablesInService.map((tb) => (
                    <option key={tb.id} value={tb.id}>
                      {tb.id} · {tb.seats} {t('seats', lang)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="field">
              <label>{t('source', lang)}</label>
              <div className="seg" style={{ height: 38 }}>
                {SOURCES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={source === s.id ? 'on' : ''}
                    onClick={() => onSourceChange(s.id)}
                    style={{ flex: 1 }}
                  >
                    {t(s.labelKey, lang)}
                  </button>
                ))}
              </div>
            </div>

            <div className="toggle-row" style={{ borderBottom: 'none', paddingBottom: 0 }}>
              <div className="tr-body">
                <div className="tr-name">Fai accomodare subito</div>
                <div className="tr-sub">
                  Crea la prenotazione già con stato <b>In sala</b>. Utile per i walk-in.
                </div>
              </div>
              <Switch
                on={seatNow}
                onToggle={() => setSeatNow((v) => !v)}
                ariaLabel="Fai accomodare subito"
              />
            </div>

            <div className="field">
              <label htmlFor="nr-note">{t('note', lang)}</label>
              <textarea
                id="nr-note"
                className="textarea"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Allergie, anniversari, richieste particolari…"
              />
            </div>

            <div className="login-foot" style={{ marginTop: 4, fontSize: 11, color: 'var(--mute)' }}>
              <span>
                {dateLabel[lang].dow} {dateLabel[lang].d} {dateLabel[lang].mon}
              </span>
            </div>

            {errMsg && (
              <div
                role="alert"
                style={{
                  marginTop: 8,
                  padding: '8px 12px',
                  border: '1px solid rgba(168, 80, 60, 0.35)',
                  borderRadius: 4,
                  background: 'var(--st-noshow-bg)',
                  color: '#8a3526',
                  fontSize: 12.5,
                }}
              >
                {errMsg}
              </div>
            )}
          </div>

          <div className="modal-foot">
            <div className="grow" />
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
              {t('cancel', lang)}
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? '…' : 'Crea prenotazione'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
